// ======================================================================
// 📁 File: WorkingAreaAssignmentCard.js
// PURPOSE:
//   Working Area selection & requirement capture (Planning Drawer)
// ======================================================================

import React, { useState } from "react";

export default function WorkingAreaAssignmentCard({
  areaType,
  availableAreas = [],

  // selected areas from operation
  selectedAreas = [],

  // refactored unified handler
  onChange,

  // legacy-safe
  onAddArea,
  onRemoveArea,
  onUpdateArea,
}) {
  const [selectedAreaId, setSelectedAreaId] = useState("");

  // ------------------------------------------------------------
  // ADD AREA (RESTORED OLD LOGIC)
  // ------------------------------------------------------------
  const handleAdd = () => {
    if (!selectedAreaId) return;

    const meta = availableAreas.find(
      (a) => a.id === Number(selectedAreaId)
    );

    if (!meta) return;

    const newArea = {
      working_area_id: meta.id,
      area_type: meta.area_type,
      required_length_m: 0,
      required_width_m: 0,
      required_area_sqm: 0,
    };

    const updated = [...selectedAreas, newArea];

    // refactored
    if (typeof onChange === "function") {
      onChange(updated);
    }

    // legacy
    if (typeof onAddArea === "function") {
      onAddArea(newArea);
    }

    setSelectedAreaId("");
  };

  // ------------------------------------------------------------
  // UPDATE DIMENSIONS
  // ------------------------------------------------------------
  const updateDimensions = (id, field, value) => {
    const numeric = Math.max(0, Number(value) || 0);

    const updated = selectedAreas.map((a) => {
      if (a.working_area_id !== id) return a;

      const length =
        field === "required_length_m"
          ? numeric
          : a.required_length_m;

      const width =
        field === "required_width_m"
          ? numeric
          : a.required_width_m;

      return {
        ...a,
        [field]: numeric,
        required_area_sqm: Number(
          (length * width).toFixed(2)
        ),
      };
    });

    if (typeof onChange === "function") {
      onChange(updated);
    }

    if (typeof onUpdateArea === "function") {
      const updatedArea = updated.find(
        (a) => a.working_area_id === id
      );
      onUpdateArea(id, updatedArea);
    }
  };

  // ------------------------------------------------------------
  // REMOVE AREA
  // ------------------------------------------------------------
  const removeArea = (id) => {
    const updated = selectedAreas.filter(
      (a) => a.working_area_id !== id
    );

    if (typeof onChange === "function") {
      onChange(updated);
    }

    if (typeof onRemoveArea === "function") {
      onRemoveArea(id);
    }
  };

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div className="card ppe-card mb-3">
      <div className="card-header">
        Working Area Requirement
        {areaType ? ` (${areaType})` : ""}
      </div>

      <div className="card-body">
        {/* ADD AREA */}
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-8">
            <label className="form-label">
              Working Area
            </label>

            <select
              className="form-select"
              value={selectedAreaId}
              onChange={(e) =>
                setSelectedAreaId(e.target.value)
              }
            >
              <option value="">
                Select Working Area
              </option>

              {availableAreas
                .filter(
                  (a) =>
                    !selectedAreas.some(
                      (sa) =>
                        sa.working_area_id === a.id
                    )
                )
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.area_name}
                  </option>
                ))}
            </select>
          </div>

          <div className="col-md-4">
            <button
              className="btn btn-outline-primary w-100"
              disabled={!selectedAreaId}
              onClick={handleAdd}
            >
              Add Area
            </button>
          </div>
        </div>

        {/* EMPTY STATE */}
        {selectedAreas.length === 0 && (
          <div className="text-muted small">
            No working areas assigned
          </div>
        )}

        {/* SELECTED AREAS */}
        {selectedAreas.map((area) => {
          const meta = availableAreas.find(
            (a) => a.id === area.working_area_id
          );

          return (
            <div
              key={area.working_area_id}
              className="border rounded p-3 mb-3"
            >
              <div className="d-flex justify-content-between mb-2">
                <strong>
                  {meta?.area_name ||
                    "Working Area"}
                </strong>

                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() =>
                    removeArea(
                      area.working_area_id
                    )
                  }
                >
                  ✕
                </button>
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">
                    Length (m)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      area.required_length_m
                    }
                    onChange={(e) =>
                      updateDimensions(
                        area.working_area_id,
                        "required_length_m",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Width (m)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      area.required_width_m
                    }
                    onChange={(e) =>
                      updateDimensions(
                        area.working_area_id,
                        "required_width_m",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Required Area (sqm)
                  </label>
                  <input
                    className="form-control"
                    readOnly
                    value={
                      area.required_area_sqm
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
